import './BusinessImpactModelInsights.scss';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { Tooltip, TooltipPayloadEntry } from 'recharts';
import {
  capitalizeWords,
  currencyFormatter,
  MedianOrMean,
  numberFormatter
} from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { ChartContainer } from '@Components/ChartContainer/ChartContainer';
import { useDynamicYAxisWidth } from '@Components/Charts/ChartUtils';
import { LineChart } from '@Components/Charts/LineChart/LineChart';
import { X_AXIS_LABEL_HEIGHT } from '@Components/Charts/XAxisLabel';
import { Size } from '@Components/designConstants';
import { DownloadDropdown } from '@Components/DownloadDropdown/DownloadDropdown';
import { InfoTooltip } from '@Components/InfoTooltip/InfoTooltip';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { WidgetContainer } from '@Components/WidgetContainer/WidgetContainer';
import { getColorMap as colors, Neutral500 } from '@src/utils/colors';
import {
  decayDataFormatterUpdated,
  saturationDataFormatter,
  weekFormat
} from '../BrandImpact/BrandImpactUtils';
import { MedianLegendLabel } from '../MedianLegendLabel';
import { EFFECT_DECAY_INFO_COPY, SATURATION_INFO_COPY } from '../modelInsightsCopy';
import { SaturationCurveChart } from '../SaturationCurveChart';
import { useSaturationDecayDownloads } from '../useSaturationDecayDownloads';

const GRAY_COLOR = Neutral500;

interface BusinessImpactModelInsightsProps {
  company: string;
  groupByLevel: string;
  // `BusinessImpactData` (bpm-types) declares these as `Record<string, any>`, but at runtime
  // `BrandEquity` passes flat arrays (`finalSaturationData`/`finalDecayData`, built via
  // `responseRows.filter().map()`) — the `Record` type is the Brand Impact sibling's shape, not
  // this page's. Left `any`: narrowing to `SaturationDataUnformatted[]`/`DecayDataUnformatted[]`
  // collides with that inaccurate bpm-types declaration at the call site.
  saturationDataUnformatted: any;
  decayDataUnformatted: any;
  queryVolumeShortLabel: string;
  centralMeasure: MedianOrMean;
}

interface BusinessSaturationTooltipProps {
  // Injected by Recharts when this element is passed as the Tooltip `content`.
  active?: boolean;
  payload?: TooltipPayloadEntry[];
  // Supplied at the call site.
  centralMeasure: MedianOrMean;
  saturationData: {
    centralMeasures: Record<string, any>;
  };
}

// Bespoke saturation-curve tooltip (Business Impact): channel row + positive
// response + central-measure positive response + revenue. Hoisted to a stable
// component and passed as an element so Recharts clones in `active`/`payload`
// (mirrors SaturationCentralMeasureDot) rather than defining a component in render.
const BusinessSaturationTooltip = ({
  active,
  payload,
  centralMeasure,
  saturationData
}: BusinessSaturationTooltipProps): ReactElement | null => {
  if (!(active && payload && payload.length)) {
    return null;
  }
  const items = payload
    .map(item => ({
      label: String(item.name),
      value: Number(item.value),
      color: item.stroke
    }))
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value));

  return (
    <div className='sat-chart-tooltip'>
      <div className='item-list'>
        {items.map((item, i) => {
          const { label, value, color } = item;
          if (!value && value !== 0) {
            return null;
          }

          return (
            <div className='item-row' key={i}>
              <div className='item-wrapper'>
                <div />
                <div className='name-row'>
                  <div className='line' style={{ backgroundColor: color }} />
                  <div className='name'>{`${
                    label.toLowerCase() === 'gqv' ? label.toUpperCase() : label
                  }: `}</div>
                </div>
              </div>
              <div className='item-wrapper'>
                <div className='sat-tooltip-header-item'>Positive Response:</div>
                <div className='value'>{parseFloat(payload[0].payload.spend).toFixed(0)}</div>
              </div>
              <div className='item-wrapper'>
                <div className='sat-tooltip-header-item'>{centralMeasure} Positive Response:</div>
                <div className='value'>
                  {parseFloat(
                    saturationData.centralMeasures[payload[0].payload.name][
                      centralMeasure.toLowerCase()
                    ] ?? 0
                  ).toFixed(0)}
                </div>
              </div>
              <div className='item-wrapper'>
                <div className='sat-tooltip-header-item'>Revenue:</div>
                <div className='value'>
                  {currencyFormatter.format(
                    payload[0].payload.response,
                    payload[0].payload.response > 10 ** 6 ? 2 : 0
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const BusinessImpactModelInsights = ({
  company,
  groupByLevel,
  saturationDataUnformatted,
  decayDataUnformatted,
  queryVolumeShortLabel,
  centralMeasure
}: BusinessImpactModelInsightsProps): ReactElement => {
  const saturationDataNew = useMemo(() => {
    if (saturationDataUnformatted.length !== 0) {
      return saturationDataFormatter(saturationDataUnformatted, centralMeasure);
    }
    return {} as any;
  }, [centralMeasure, saturationDataUnformatted]);

  const decayDataNew = useMemo(() => {
    if (decayDataUnformatted.length !== 0) {
      let finalDecayData;
      switch (groupByLevel) {
        case 'channel':
          finalDecayData = decayDataFormatterUpdated(
            decayDataUnformatted,
            'spend_carryover',
            groupByLevel
          );
          break;
        default:
          finalDecayData = decayDataFormatterUpdated(
            decayDataUnformatted,
            'spend_carryover',
            groupByLevel
          );
          break;
      }
      for (const line of finalDecayData.lines) {
        if (line.dataKey === 'gqv') {
          line.dataKey = queryVolumeShortLabel.toLowerCase();
          line.name = queryVolumeShortLabel;
        }
      }
      for (const data of finalDecayData.newD) {
        if (data.gqv) {
          data[queryVolumeShortLabel.toLowerCase()] = data.gqv;
        }
      }

      return finalDecayData;
    }
    return [];
  }, [decayDataUnformatted, groupByLevel, queryVolumeShortLabel]);

  const [activeLine, setActiveLine] = useState(null);
  const [hoveredLegend, setHoveredLegend] = useState(null);

  const { xAxisHeight, yAxisWidth, setChartRef } = useDynamicYAxisWidth({
    xAxisHeightModifier: x => x + X_AXIS_LABEL_HEIGHT + 5,
    yAxisWidthModifier: x => x + 3
  });

  const colorMap = useMemo(() => {
    if (Object.keys(saturationDataNew).length !== 0) {
      return colors(saturationDataNew.newD);
    }
    return undefined;
  }, [saturationDataNew]);

  const handleLegendClick = useCallback(
    line => {
      setActiveLine(activeLine === line.name ? null : line.name);
    },
    [activeLine]
  );

  const handleLegendEnter = useCallback(
    line => {
      setHoveredLegend(hoveredLegend === line.name ? null : line.name);
    },
    [hoveredLegend]
  );

  const tooltip = (
    <Tooltip
      content={
        <BusinessSaturationTooltip
          centralMeasure={centralMeasure}
          saturationData={saturationDataNew}
        />
      }
      isAnimationActive={false}
    />
  );

  const { excelDownloadSat, pngDownloadSat, excelDownloadDecay, pngDownloadDecay } =
    useSaturationDecayDownloads(
      company,
      saturationDataUnformatted,
      decayDataUnformatted,
      '.business-impact-model-insights'
    );

  return (
    <WidgetContainer
      collapsible
      header='Model Insights'
      subHeader='The model estimation process yields business insights related to channel saturation and the lagged nature of consumer response.'
    >
      {
        <div className='business-impact-model-insights'>
          {Object.keys(saturationDataNew).length !== 0 && (
            <div className='left'>
              <ChartContainer
                title='Saturation Curves (Revenue)'
                rightActions={
                  <>
                    <InfoTooltip size={Size.MD}>{SATURATION_INFO_COPY}</InfoTooltip>
                    <DownloadDropdown onClickOptions={[excelDownloadSat, pngDownloadSat]} />
                  </>
                }
              >
                <>
                  <div className='sat-wrapper'>
                    <SaturationCurveChart
                      title='Revenue'
                      setChartRef={setChartRef}
                      xAxisHeight={xAxisHeight}
                      yAxisWidth={yAxisWidth}
                      xAxis={{ tickFormatter: val => String(val) }}
                      yAxis={{ tickFormatter: val => currencyFormatter.format(val, 0) }}
                      saturationData={saturationDataNew}
                      centralMeasure={centralMeasure}
                      colorMap={colorMap}
                      activeLine={activeLine}
                      hoveredLegend={hoveredLegend}
                      tooltip={tooltip}
                      showTooltip
                      xAxisLabel='Positive Response'
                    />
                  </div>
                  <div className='right-of-chart'>
                    <div className='legend'>
                      <div className='business-impact-model-header'>Brand Health Metric</div>
                      {saturationDataNew.newD.map(item => {
                        const isActiveLine = activeLine === null ? true : activeLine === item.name;
                        const isHoveredLegend =
                          hoveredLegend === null ? true : hoveredLegend === item.name;
                        const activeColor = isActiveLine ? colorMap[item.name] : GRAY_COLOR;
                        const hoverColor = isHoveredLegend ? colorMap[item.name] : GRAY_COLOR;
                        return (
                          <InteractiveDiv
                            className='legend-item'
                            key={item.name || item.dataKey}
                            onClick={() => handleLegendClick(item)}
                            onMouseEnter={() => handleLegendEnter(item)}
                            onMouseLeave={() => setHoveredLegend(null)}
                          >
                            <div
                              className='line'
                              style={{
                                backgroundColor: `${activeLine ? activeColor : hoverColor}`
                              }}
                            />
                            <div className='legend-value'>
                              <div className={isActiveLine ? 'active-legend' : 'inactive-legend'}>
                                {/* Gqv is an exception to our normal camel case */}
                                {item.name.replace(', ', ' - ').replace(/Gqv/g, 'GQV')}
                              </div>
                            </div>
                          </InteractiveDiv>
                        );
                      })}
                    </div>
                    <MedianLegendLabel>
                      {centralMeasure} <br /> Positive <br /> Response
                    </MedianLegendLabel>
                  </div>
                </>
              </ChartContainer>
            </div>
          )}
          {Object.keys(decayDataNew).length !== 0 && (
            <div className='right'>
              <ChartContainer
                rightActions={
                  <>
                    <InfoTooltip size={Size.MD}>{EFFECT_DECAY_INFO_COPY}</InfoTooltip>
                    <DownloadDropdown onClickOptions={[excelDownloadDecay, pngDownloadDecay]} />
                  </>
                }
                title='Effect Decays (Revenue)'
              >
                <div className='decay-wrapper'>
                  <div className='decay-chart-title'>Effect (Revenue)</div>
                  {/* Brand-Health legend literals: match prod exactly and have no palette
                      equivalent (Channel1-4 are close but not identical). */}
                  <LineChart
                    customTooltipHeader={val => `Week ${weekFormat(val)} Effect Carryover`}
                    data={decayDataNew?.newD}
                    lineDataKeys={decayDataNew?.lines}
                    tooltipLabelFormatter={val =>
                      capitalizeWords(val === 'gqv' ? queryVolumeShortLabel : val)
                    }
                    tooltipFormatter={val => (val ? `${numberFormatter.format(val, 0)}%` : val)}
                    tooltipShape='line'
                    usePercentageYAxis
                    xAxisDataKey='Index'
                    xAxisTickFormatter={val => `Week ${weekFormat(val)}`}
                    usePrimaryColors={false}
                    legendTitle='Brand Health Metric'
                    xAxisLabel='Week'
                    colorMappings={[
                      { name: 'Awareness', color: '#00D9F2' },
                      { name: 'Buzz', color: '#88F252' },
                      { name: 'Consideration', color: '#DFF13E' },
                      { name: 'Intent', color: '#1980FA' }
                    ]}
                  />
                </div>
              </ChartContainer>
            </div>
          )}
        </div>
      }
    </WidgetContainer>
  );
};
